import { Paper, Avatar, Typography, Stack, IconButton } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';

type LinkType = 'research' | 'instagram' | 'x' | 'linkedin' | 'github';

export interface SocialLink {
  type: LinkType;
  url: string;
}

interface PersonCardProps {
  name: string;
  role: string;
  organisation?: string;
  bio?: string;
  /** Alias for `bio` — the about page data uses `description`. Both are accepted. */
  description?: string;
  image?: string;
  links?: SocialLink[];
  variant?: 'team' | 'advisor';
}

const getIcon = (type: LinkType) => {
  switch (type) {
    case 'research':
      return <SchoolIcon fontSize="small" />;
    case 'instagram':
      return <InstagramIcon fontSize="small" />;
    case 'x':
      return <XIcon fontSize="small" />;
    case 'linkedin':
      return <LinkedInIcon fontSize="small" />;
    case 'github':
      return <GitHubIcon fontSize="small" />;
    default:
      return <LinkedInIcon fontSize="small" />;
  }
};

export function PersonCard({
  name,
  role,
  organisation,
  bio,
  description,
  image,
  links,
  variant = 'team',
}: PersonCardProps) {
  const isAdvisor = variant === 'advisor';
  const bioText = bio ?? description;

  return (
    <Paper
      elevation={0}
      sx={{
        p: isAdvisor ? 3 : 4,
        textAlign: 'center',
        height: '100%',
        bgcolor: isAdvisor ? 'rgba(255, 255, 255, 0.5)' : 'white',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Avatar
        src={image}
        alt={name}
        sx={{
          width: isAdvisor ? 80 : 120,
          height: isAdvisor ? 80 : 120,
          mx: 'auto',
          mb: 2,
          bgcolor: 'primary.main',
          fontSize: isAdvisor ? '1.5rem' : '2rem',
        }}
      >
        {name.split(' ').map((n) => n[0]).join('')}
      </Avatar>
      <Typography variant="h6" component="h4" sx={{ mb: 0.5 }}>
        {name}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        sx={{
          color: 'primary.main',
          fontWeight: 600,
          mb: organisation ? 0.25 : bioText ? 1 : links ? 2 : 0,
        }}
      >
        {role}
      </Typography>
      {organisation && (
        <Typography
          variant="caption"
          component="p"
          color="text.secondary"
          sx={{ mb: bioText ? 1 : links ? 2 : 0 }}
        >
          {organisation}
        </Typography>
      )}
      {bioText && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {bioText}
        </Typography>
      )}
      {links && links.length > 0 && (
        <Stack direction="row" spacing={1} justifyContent="center">
          {links.map((link) => (
            <IconButton
              key={link.type}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label={`${name} on ${link.type}`}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {getIcon(link.type)}
            </IconButton>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
